import { Button, VStack, useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiResource } from '@texas/api/hooks/useApiResource';
import { branchRegionsApi } from '@texas/api/endpoints/branchRegionsApi';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { branchRegionTableColumns } from './Columns';
import { CreateBranchRegion } from './CreateBranchRegion';
import { EditBranchRegion } from './EditBranchRegion';

export function BranchRegions() {
  const {
    onOpen: onCreateOpen,
    onClose: onCreateClose,
    isOpen: isCreateOpen,
  } = useDisclosure();
  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();

  const { t } = useTranslation();

  const {
    data: branchRegions,
    refetch,
    error,
    loading,
  } = useApiResource(branchRegionsApi.getAll);

  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const columns = useMemo(() => {
    return branchRegionTableColumns(t, onEditOpen);
  }, [t, onEditOpen]);

  const handleCreated = useCallback(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const handleUpdated = useCallback(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  return (
    <ConfigurationPage
      title={t('branchRegion.title')}
      toolbar={
        <SearchFilterInput
          value={searchQuery}
          style="entityDirectory"
          onChange={setSearchQuery}
        />
      }
      action={
        <Button variant="entityDirectoryPrimary" onClick={onCreateOpen}>
          {t('general.addNew')}
        </Button>
      }
    >
      <VStack spacing={4} align="stretch">
        <DataTableContainer
          w="100%"
          datatable={{
            data: branchRegions ?? [],
            isLoading: loading,
            columns: columns,
            variant: 'entity-directory',
          }}
          error={error}
        />
      </VStack>

      {isCreateOpen && (
        <CreateBranchRegion
          isOpen={isCreateOpen}
          onClose={onCreateClose}
          onCreated={handleCreated}
        />
      )}

      {editValue && (
        <EditBranchRegion
          isOpen={isEditOpen}
          onClose={onEditClose}
          onUpdated={handleUpdated}
          branchRegionId={editValue}
        />
      )}
    </ConfigurationPage>
  );
}
